/* RESPONSIBLE TEAM: team-standalone */

import { tracked } from '@glimmer/tracking';
import {
  StandalonePlatformToIcon,
  type StandalonePlatform,
} from 'embercom/objects/standalone/constants';
import { isPresent } from '@ember/utils';
import dataFormatMap from 'embercom/models/data/attributes/data-format-map';
import type Attribute from 'embercom/models/attribute';
import { type UserField } from 'embercom/objects/standalone/data-fields/zendesk-user-field';
import { type OrganizationField } from 'embercom/objects/standalone/data-fields/zendesk-organization-field';
import { type IntercomFieldType } from 'embercom/models/standalone/external-field-identifier';
import type ExternalFieldIdentifier from 'embercom/models/standalone/external-field-identifier';

export type FieldType =
  | 'text'
  | 'textarea'
  | 'checkbox'
  | 'date'
  | 'integer'
  | 'decimal'
  | 'regexp'
  | 'dropdown'
  | 'multiselect'
  | 'lookup'
  | 'tagger'
  | 'id'
  | 'string'
  | 'address'
  | 'email'
  | 'phone'
  | 'boolean'
  | 'datetime'
  | 'url'
  | 'double'
  | 'picklist'
  | 'priority'
  | 'tickettype';

export type IntercomAttributeType = 'string' | 'integer' | 'float' | 'boolean' | 'date';
export interface TicketFieldResponse {
  id: string;
  name: string;
  description?: string;
  type: FieldType;
  required: boolean;
  createable?: boolean;
  updateable?: boolean;
  intercom_cvda?: {
    id: string;
  };
  built_in?: boolean;
}

export class TicketField {
  id: string;
  name: string;
  description?: string;
  type: FieldType;
  required: boolean;
  createable?: boolean;
  updateable?: boolean;
  @tracked isSynchronizing?: boolean = false;
  intercom_cvda?: {
    id: string;
  };
  builtIn: boolean;

  constructor(response: TicketFieldResponse) {
    this.id = response.id;
    this.name = response.name;
    this.description = response.description;
    this.type = response.type;
    this.required = response.required;
    this.intercom_cvda = response.intercom_cvda;
    this.createable = response.createable;
    this.updateable = response.updateable;
    this.builtIn = response.built_in || false;
  }

  get intercomFieldType(): IntercomFieldType {
    return 'conversation';
  }

  get synchronized() {
    return this.builtIn || isPresent(this.intercom_cvda?.id);
  }

  get intercomCdaIdentifier() {
    return this.intercom_cvda?.id;
  }

  get intercomAttributeDescriptorIcon() {
    switch (this.type) {
      case 'boolean':
      case 'checkbox':
        return 'boolean';
      case 'integer':
      case 'decimal':
      case 'double':
        return 'numbers';
      case 'datetime':
      case 'date':
        return 'calendar';
      case 'picklist':
      case 'dropdown':
      case 'multiselect':
      case 'priority':
      case 'tickettype':
        return 'list';
      default:
        return 'small-text';
    }
  }

  get intercomDataType() {
    switch (this.type) {
      case 'boolean':
      case 'checkbox':
        return 'boolean';
      case 'integer':
        return 'integer';
      case 'decimal':
      case 'double':
        return 'float';
      case 'datetime':
      case 'date':
        return 'date';
      case 'picklist':
      case 'dropdown':
      case 'multiselect':
      case 'priority':
      case 'tickettype':
        return 'conversation_attribute_list';
      default:
        return 'string';
    }
  }
}

export class StandaloneUserAttribute {
  isSyncedWithPlatform: boolean;
  standalonePlatform?: StandalonePlatform;
  userField?: UserField | ExternalFieldIdentifier;
  icon?: string;
  name: string;
  intercomAttribute: any;
  formatName?: string;

  constructor({
    standalonePlatform,
    intercomAttribute,
    userField,
  }: {
    standalonePlatform?: StandalonePlatform;
    intercomAttribute?: any;
    userField?: UserField | ExternalFieldIdentifier;
  }) {
    this.isSyncedWithPlatform = isPresent(standalonePlatform);
    this.standalonePlatform = standalonePlatform;
    this.intercomAttribute = intercomAttribute;
    this.userField = userField;
    this.name = userField?.name || intercomAttribute?.name;
    this.icon = getIcon(standalonePlatform, intercomAttribute);
    this.formatName = getFormatName(userField);
  }
}

export class StandaloneCompanyAttribute {
  isSyncedWithPlatform: boolean;
  standalonePlatform?: StandalonePlatform;
  field?: OrganizationField | ExternalFieldIdentifier;
  icon?: string;
  name: string;
  intercomAttribute?: Attribute;
  formatName?: string;

  constructor({
    standalonePlatform,
    intercomAttribute,
    field,
  }: {
    standalonePlatform?: StandalonePlatform;
    intercomAttribute?: Attribute;
    field?: OrganizationField | ExternalFieldIdentifier;
  }) {
    this.isSyncedWithPlatform = isPresent(standalonePlatform);
    this.standalonePlatform = standalonePlatform;
    this.intercomAttribute = intercomAttribute;
    this.field = field;
    this.name = field?.name || intercomAttribute!.name;
    this.icon = getIcon(standalonePlatform, intercomAttribute);
    this.formatName = getFormatName(field);
  }
}

function getIcon(
  standalonePlatform: StandalonePlatform | undefined,
  intercomAttribute: Attribute | undefined,
) {
  return (
    (standalonePlatform && StandalonePlatformToIcon[standalonePlatform]) || intercomAttribute?.icon
  );
}

function getFormatName(
  field: UserField | OrganizationField | ExternalFieldIdentifier | undefined,
): string | undefined {
  if (field && field.builtIn && field.intercomSdaIdentifier) {
    let dataType: string | undefined;
    if ('intercom_sda' in field) {
      dataType = field.intercom_sda?.type;
    } else {
      dataType = field.type;
    }
    return (
      dataFormatMap[dataType as keyof typeof dataFormatMap] || 'settings.data-format-map.unknown'
    );
  }
  return 'settings.data-format-map.unknown';
}

export class StandaloneTicketAttribute {
  standalonePlatform: StandalonePlatform;
  ticketField: TicketField;
  intercomCvda?: any;
  icon: string | undefined;
  name: string;

  constructor({
    standalonePlatform,
    ticketField,
    intercomCvda,
  }: {
    standalonePlatform: StandalonePlatform;
    ticketField: TicketField;
    intercomCvda?: any;
  }) {
    this.standalonePlatform = standalonePlatform;
    this.ticketField = ticketField;
    this.name = ticketField.name;
    this.icon = StandalonePlatformToIcon[standalonePlatform];
    this.intercomCvda = intercomCvda;
  }
}
